/* eslint-disable @typescript-eslint/no-explicit-any */
import { AuditInfoCard } from '../../components/common/audit-info-card';
import { Rise, SectionLabel, StatusChip } from '../../components/common/panel';
import { PANEL, PRETTY } from '../../components/common/ui-kit';
import type { EvalCase } from '../../types';

const asText = (v: unknown): string =>
  v === null || v === undefined ? '-' : typeof v === 'string' ? v : JSON.stringify(v, null, 2);

/** The case's identity chips: what it runs on, its kind, its version. The page
 *  title and the dialog header both carry them. */
export const EvalCaseChips = ({ data }: { data: EvalCase }) => (
  <>
    {(Array.isArray(data.type) ? data.type : [data.type]).filter(Boolean).map((t) => (
      <StatusChip key={t} tone="info" dot={false}>{t}</StatusChip>
    ))}
    {data.caseType && <StatusChip tone="warn" dot={false}>{data.caseType}</StatusChip>}
    {data.version != null && <StatusChip tone="neutral" dot={false} className="tabular-nums">v{data.version}</StatusChip>}
  </>
);

/**
 * Presentational body of an eval case — shared by the full-page view
 * (eval-case-view) and the in-place dialog (eval-case-view-dialog) so both
 * always render the case identically. The page owns its title and actions.
 *
 * `embedded` is the dialog's shape: the dialog's own header already carries the
 * name and code, and it is itself a raised surface, so repeating the title block
 * and nesting a shadowed panel inside it would read as a card inside a card.
 * Record Information goes too — a case opened from a run is being read for what
 * it asserts, not for who last touched it.
 */
const EvalCaseDetails = ({ data, embedded = false }: { data: EvalCase; embedded?: boolean }) => {
  const body = (
    <div className="flex flex-col gap-5">
      {embedded && <div className="flex flex-wrap items-center gap-1.5"><EvalCaseChips data={data} /></div>}
      {!!data.tags?.length && (
        <div>
          <SectionLabel icon="pi pi-tags">Tags</SectionLabel>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {data.tags.map((t) => <StatusChip key={t} tone="neutral" dot={false}>{t}</StatusChip>)}
          </div>
        </div>
      )}
      {data.description && (
        <div>
          <SectionLabel icon="pi pi-align-left">Description</SectionLabel>
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-gray-700 dark:text-gray-300" style={PRETTY}>{data.description}</p>
        </div>
      )}
      {data.scoring && (
        <div data-testid="EvalCase.Scoring">
          <SectionLabel icon="pi pi-chart-bar">Scored by</SectionLabel>
          {!data.scoring.length && (
            <p className="mt-1.5 text-sm text-ink-muted">Not in any suite yet, so nothing scores it.</p>
          )}
          <div className="mt-2 flex flex-col gap-3">
            {data.scoring.map((s) => {
              const gaps = s.metrics.filter((m) => m.outcome);
              return (
                <div key={s.suiteId}>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {s.name || s.suiteCode} <span className="font-normal text-ink-muted">· {s.suiteCode} · {s.type}</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {s.metrics.map((m) => (
                      <span key={m.key} title={m.outcome ? `Needs ${m.needs}` : `Scored with ${m.graderType}`}>
                        <StatusChip dot={false}
                          tone={m.outcome === 'error' ? 'danger' : m.outcome ? 'neutral' : 'success'}
                          className={m.outcome === 'skipped' ? 'opacity-60 line-through' : ''}>{m.key}</StatusChip>
                      </span>
                    ))}
                  </div>
                  {gaps.map((m) => (
                    <p key={m.key} className="mt-1 text-xs text-ink-muted">
                      {m.key} needs {m.needs} — {m.outcome === 'error'
                        ? 'without it this case is marked errored in this suite.'
                        : 'without it the metric is left out of this case\'s score.'}
                    </p>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="min-w-0">
          <SectionLabel icon="pi pi-sign-in">
            {(Array.isArray(data.type) ? data.type : [data.type]).every((t) => t === 'agent') ? 'Task' : 'Question / input'}
          </SectionLabel>
          <pre className="ui-inset mt-1.5 whitespace-pre-wrap break-words p-3 font-sans text-sm leading-relaxed text-gray-800 dark:text-gray-200">{asText(data.input)}</pre>
        </div>
        <div className="min-w-0">
          <SectionLabel icon="pi pi-flag">Expected result</SectionLabel>
          <pre className="ui-inset mt-1.5 whitespace-pre-wrap break-words p-3 font-sans text-sm leading-relaxed text-gray-800 dark:text-gray-200">{asText(data.expectedResult)}</pre>
        </div>
      </div>
      {!!data.requiredFacts?.length && (
        <div>
          <SectionLabel icon="pi pi-check-square">Required facts</SectionLabel>
          <ul className="mt-1.5 list-inside list-disc text-sm leading-relaxed text-gray-800 dark:text-gray-200">
            {data.requiredFacts.map((fact, i) => <li key={i}>{fact}</li>)}
          </ul>
        </div>
      )}
      {!!data.expectedSources?.length && (
        <div>
          <SectionLabel icon="pi pi-link">Expected sources</SectionLabel>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {data.expectedSources.map((sourceItem) => (
              <StatusChip key={sourceItem.sourceRef} tone="neutral" dot={false} className="font-mono">{sourceItem.sourceRef}</StatusChip>
            ))}
          </div>
        </div>
      )}
      {/* An agent case's whole assertion. It was never rendered, so the view page
          of an agent case showed a question and nothing it was graded on. */}
      {!!data.expectedToolCalls?.length && (
        <div data-testid="EvalCase.ExpectedToolCalls">
          <SectionLabel icon="pi pi-wrench">Expected tool calls</SectionLabel>
          <div className="mt-2 flex flex-col gap-1.5">
            {(data.expectedToolCalls as any[]).map((call, i) => (
              <div key={`${call?.name}-${i}`} className="flex flex-wrap items-center gap-2">
                {call?.order != null && <span className="w-5 text-right text-xs tabular-nums text-gray-400">{call.order}</span>}
                <StatusChip tone="info" dot={false} className="font-mono">{call?.name}</StatusChip>
                {call?.arguments
                  ? <code className="ui-inset px-2 py-0.5 text-xs">{JSON.stringify(call.arguments)}</code>
                  : <span className="text-xs text-ink-muted">any arguments — judged against the task</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      {!!(data as any).tools?.length && (
        <div>
          <SectionLabel icon="pi pi-box">Tools offered</SectionLabel>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {((data as any).tools as any[]).map((t, i) => (
              <span key={`${t?.name}-${i}`} title={t?.description || ''}>
                <StatusChip tone="neutral" dot={false} className="font-mono">{t?.name}</StatusChip>
              </span>
            ))}
          </div>
        </div>
      )}
      {!!data.context?.length && (
        <div>
          <SectionLabel icon="pi pi-book">Ground-truth context</SectionLabel>
          {data.context.map((passage, i) => (
            <pre key={i} className="ui-inset mt-1.5 whitespace-pre-wrap break-words p-3 font-sans text-sm leading-relaxed text-gray-800 dark:text-gray-200">{passage}</pre>
          ))}
        </div>
      )}
    </div>
  );

  if (embedded) return <Rise>{body}</Rise>;

  return (
    <div className="grid grid-cols-1 gap-3">
      <Rise i={1} className={`${PANEL} p-4`}>{body}</Rise>
      <Rise i={2}>
        <AuditInfoCard
          surface="panel"
          createdAt={(data as any).createdAt}
          updatedAt={(data as any).updatedAt}
          createdBy={data.createdBy}
          updatedBy={data.updatedBy}
        />
      </Rise>
    </div>
  );
};

export default EvalCaseDetails;
